'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { ImageUploadDropzone } from '@/components/ui/ImageUploadDropzone';
import { StaffItem, ScheduleItem } from './StaffScheduleCards';
import {
  User,
  Clock,
  Scissors,
  Mail,
  Phone,
  Briefcase,
  Star,
  Check,
  AlertCircle
} from 'lucide-react';

export interface StaffDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  staff: StaffItem | null;
  servicesList?: { id: string; name: string; duration: number; cashPrice: number }[];
  onSaved: () => void;
}

export function StaffDrawer({
  isOpen,
  onClose,
  staff,
  servicesList = [],
  onSaved
}: StaffDrawerProps) {
  const [activeTab, setActiveTab] = useState<'profile' | 'schedules' | 'services'>('profile');

  // Form state
  const [name, setName] = useState('');
  const [roleTitle, setRoleTitle] = useState('Profesional');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [colorHex, setColorHex] = useState('#6255F5');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [active, setActive] = useState(true);
  const [isFeatured, setIsFeatured] = useState(false);
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultDaySchedules: ScheduleItem[] = [
    { id: 'new-1', dayOfWeek: 1, dayName: 'Lunes', enabled: true, startTime: '09:00', endTime: '19:00' },
    { id: 'new-2', dayOfWeek: 2, dayName: 'Martes', enabled: true, startTime: '09:00', endTime: '19:00' },
    { id: 'new-3', dayOfWeek: 3, dayName: 'Miércoles', enabled: true, startTime: '09:00', endTime: '19:00' },
    { id: 'new-4', dayOfWeek: 4, dayName: 'Jueves', enabled: true, startTime: '09:00', endTime: '19:00' },
    { id: 'new-5', dayOfWeek: 5, dayName: 'Viernes', enabled: true, startTime: '09:00', endTime: '20:00' },
    { id: 'new-6', dayOfWeek: 6, dayName: 'Sábado', enabled: true, startTime: '09:00', endTime: '18:00' },
    { id: 'new-0', dayOfWeek: 0, dayName: 'Domingo', enabled: false, startTime: '09:00', endTime: '13:00' }
  ];

  const colorPalette = [
    { label: 'Violeta Calenddy', hex: '#6255F5' },
    { label: 'Esmeralda', hex: '#10B981' },
    { label: 'Azul Real', hex: '#2563EB' },
    { label: 'Ámbar', hex: '#D97706' },
    { label: 'Rosa Magenta', hex: '#E11D48' },
    { label: 'Gris Carbón', hex: '#475569' }
  ];

  useEffect(() => {
    if (staff) {
      setName(staff.name || '');
      setRoleTitle(staff.roleTitle || 'Profesional');
      setEmail(staff.email || '');
      setPhone(staff.phone || '');
      setColorHex(staff.colorHex || '#6255F5');
      setAvatarUrl(staff.avatarUrl || '');
      setActive(staff.active !== undefined ? staff.active : true);
      setIsFeatured(Boolean(staff.isFeatured));
      setSchedules(staff.schedules && staff.schedules.length > 0 ? staff.schedules : defaultDaySchedules);
      // Extraer IDs de servicios si vienen populados
      if ((staff as any).services && Array.isArray((staff as any).services)) {
        setSelectedServiceIds((staff as any).services.map((s: any) => s.id));
      }
    } else {
      setName('');
      setRoleTitle('Profesional');
      setEmail('');
      setPhone('');
      setColorHex('#6255F5');
      setAvatarUrl('');
      setActive(true);
      setIsFeatured(false);
      setSchedules(defaultDaySchedules);
      setSelectedServiceIds(servicesList.map((s) => s.id));
    }
    setError(null);
    setActiveTab('profile');
  }, [staff, isOpen, servicesList]);

  const handleToggleDay = (dayOfWeek: number) => {
    setSchedules((prev) =>
      prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, enabled: !s.enabled } : s))
    );
  };

  const handleTimeChange = (dayOfWeek: number, field: 'startTime' | 'endTime', value: string) => {
    setSchedules((prev) =>
      prev.map((s) => (s.dayOfWeek === dayOfWeek ? { ...s, [field]: value } : s))
    );
  };

  const toggleServiceSelection = (serviceId: string) => {
    setSelectedServiceIds((prev) =>
      prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
    );
  };

  const selectAllServices = () => {
    setSelectedServiceIds(servicesList.map((s) => s.id));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('El nombre del profesional es obligatorio');
      setActiveTab('profile');
      return;
    }

    setLoading(true);
    setError(null);

    const payload = {
      id: staff ? staff.id : undefined,
      name: name.trim(),
      roleTitle: roleTitle.trim() || 'Profesional',
      email: email.trim() || null,
      phone: phone.trim() || null,
      avatarUrl: avatarUrl.trim() || null,
      colorHex,
      active,
      isFeatured,
      schedules: schedules.map((sc) => ({
        id: sc.id.startsWith('new-') ? undefined : sc.id,
        dayOfWeek: sc.dayOfWeek,
        dayName: sc.dayName,
        enabled: sc.enabled,
        startTime: sc.startTime,
        endTime: sc.endTime
      }))
    };

    try {
      const url = '/api/staff';
      const method = staff ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      if (data.success) {
        onSaved();
        onClose();
      } else {
        setError(data.error || 'Error al guardar el profesional');
      }
    } catch (err: any) {
      setError('Error de conexión con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={staff ? 'Editar Profesional' : 'Nuevo Profesional'}
      subtitle={staff ? `Configurando a "${staff.name}"` : 'Agregá un nuevo miembro al equipo'}
      footer={
        <div className="flex items-center justify-between w-full">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={loading}>
            {staff ? 'Guardar Cambios' : 'Crear Profesional'}
          </Button>
        </div>
      }
    >
      {/* Error alert */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-rose-700 text-xs font-medium">
          <AlertCircle className="w-4 h-4 shrink-0 text-rose-500" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center border-b border-slate-150 gap-2">
        <button
          type="button"
          onClick={() => setActiveTab('profile')}
          className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
            activeTab === 'profile'
              ? 'border-[#6255F5] text-[#6255F5]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Datos Generales
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('schedules')}
          className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
            activeTab === 'schedules'
              ? 'border-[#6255F5] text-[#6255F5]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Horarios Semanales
        </button>
        {servicesList.length > 0 && (
          <button
            type="button"
            onClick={() => setActiveTab('services')}
            className={`pb-2.5 px-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${
              activeTab === 'services'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            Servicios ({selectedServiceIds.length})
          </button>
        )}
      </div>

      {/* TAB 1: Datos Generales */}
      {activeTab === 'profile' && (
        <div className="space-y-4">
          <Input
            label="Nombre Completo *"
            placeholder="Ej: Marcos Ramírez, Lucas Giménez"
            value={name}
            onChange={(e) => setName(e.target.value)}
            leftIcon={<User className="w-4 h-4" />}
          />

          <Input
            label="Rol o Especialidad"
            placeholder="Ej: Barbero Principal, Estilista, Colorista"
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            leftIcon={<Briefcase className="w-4 h-4" />}
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Teléfono Móvil"
              type="tel"
              placeholder="11 3176-7548"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              leftIcon={<Phone className="w-4 h-4" />}
            />

            <Input
              label="Correo Electrónico"
              type="email"
              placeholder="empleado@negocio.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              leftIcon={<Mail className="w-4 h-4" />}
            />
          </div>

          {/* Color Identificador */}
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-2">
              Color en la Agenda
            </label>
            <div className="flex items-center gap-2.5">
              {colorPalette.map((c) => (
                <button
                  key={c.hex}
                  type="button"
                  onClick={() => setColorHex(c.hex)}
                  style={{ backgroundColor: c.hex }}
                  className={`w-7 h-7 rounded-full transition-transform cursor-pointer flex items-center justify-center text-white ${
                    colorHex === c.hex ? 'ring-3 ring-offset-2 ring-[#6255F5] scale-110 shadow-sm' : 'opacity-80 hover:opacity-100'
                  }`}
                  title={c.label}
                >
                  {colorHex === c.hex && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                </button>
              ))}
            </div>
          </div>

          {/* Foto de Perfil */}
          <div className="pt-2">
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Foto o Avatar (Opcional)
            </label>
            <ImageUploadDropzone
              value={avatarUrl}
              onChange={setAvatarUrl}
              aspect="square"
              isRoundAvatar
              label="Foto del profesional"
            />
          </div>

          {/* Switches */}
          <div className="space-y-3 pt-2">
            <Switch
              checked={active}
              onChange={setActive}
              label="Profesional Activo"
              description="Aparece disponible en la agenda y portal para recibir turnos"
            />

            <Switch
              checked={isFeatured}
              onChange={setIsFeatured}
              label="Destacado en Portal Público"
              description="Sugerido prioritariamente a los clientes"
            />
          </div>
        </div>
      )}

      {/* TAB 2: Horarios Semanales */}
      {activeTab === 'schedules' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500 font-medium">
            Configurá los días y horarios en los que este profesional atiende al público:
          </p>

          <div className="divide-y divide-slate-150 border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-2xs">
            {schedules.map((sc) => (
              <div
                key={sc.dayOfWeek}
                className={`p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-colors ${
                  sc.enabled ? 'bg-white' : 'bg-slate-50/70 opacity-70'
                }`}
              >
                {/* Day name & toggle switch */}
                <div className="flex items-center gap-3 min-w-[130px]">
                  <Switch
                    checked={sc.enabled}
                    onChange={() => handleToggleDay(sc.dayOfWeek)}
                  />
                  <span
                    className={`text-xs font-bold ${
                      sc.enabled ? 'text-slate-900' : 'text-slate-400 line-through'
                    }`}
                  >
                    {sc.dayName}
                  </span>
                </div>

                {/* Time Pickers */}
                {sc.enabled ? (
                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    <input
                      type="time"
                      value={sc.startTime}
                      onChange={(e) => handleTimeChange(sc.dayOfWeek, 'startTime', e.target.value)}
                      className="h-9 px-2.5 text-xs font-medium bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#6255F5] focus:ring-1 focus:ring-[#6255F5]"
                    />
                    <span className="text-xs text-slate-400 font-medium">a</span>
                    <input
                      type="time"
                      value={sc.endTime}
                      onChange={(e) => handleTimeChange(sc.dayOfWeek, 'endTime', e.target.value)}
                      className="h-9 px-2.5 text-xs font-medium bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#6255F5] focus:ring-1 focus:ring-[#6255F5]"
                    />
                  </div>
                ) : (
                  <span className="text-xs text-slate-400 font-medium italic">
                    Franco / No laborable
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 3: Servicios Asignados */}
      {activeTab === 'services' && servicesList.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500 font-medium">
              Marcá qué servicios del catálogo realiza este profesional:
            </p>
            <button
              type="button"
              onClick={selectAllServices}
              className="text-xs text-[#6255F5] font-semibold hover:underline cursor-pointer"
            >
              Marcar todos
            </button>
          </div>

          <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white">
            {servicesList.map((svc) => {
              const isSelected = selectedServiceIds.includes(svc.id);

              return (
                <div
                  key={svc.id}
                  onClick={() => toggleServiceSelection(svc.id)}
                  className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                    isSelected ? 'bg-[#F4F3FF]/40' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                      <Scissors className="w-3.5 h-3.5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-900">{svc.name}</p>
                      <p className="text-[11px] text-slate-400">
                        {svc.duration} min · ${svc.cashPrice.toLocaleString('es-AR')}
                      </p>
                    </div>
                  </div>

                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="w-4 h-4 rounded text-[#6255F5] accent-[#6255F5] cursor-pointer"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SlideOverDrawer>
  );
}
